import { OutlineButton } from 'components/Button';
import SearchIcon from 'components/icons/SearchIcon';
import Container from 'components/layout/Container';
import Layout from 'containers/Layout';
import contentful from 'lib/contentful';
import { SiteSettingsData } from 'lib/contentful/types';
import sanfordApi, { SFSearchResult } from 'lib/sanford-api';
import { GetStaticProps, InferGetStaticPropsType, NextPage } from 'next';
import classnames from 'utils/classnames';
import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from 'react-query';
import RecipeCard from 'components/card/RecipeCard';
import ProductCard from 'components/card/ProductCard';
import Link from 'components/Link';
import SpinnerIcon from 'components/icons/SpinnerIcon';
import { useRouter } from 'next/router';
import { REVALIDATION_TIME } from '../lib/constants';

type ResultSectionProps = {
  count: number;
  title: string;
  children?: React.ReactNode;
};
const ResultSection: React.FC<ResultSectionProps> = (props) => {
  const { title, count } = props;
  return (
    <Container className="mb-12 sm:mb-16 ">
      <div className="border-b border-sf-blue-700/5 pb-12 text-sf-blue-700 sm:pb-16">
        <div className="mb-5 sm:mb-8">
          <h2 className="mb-2 text-2xl uppercase">{title}</h2>
          <span className="text-lg">
            {count > 0 ? (
              <>
                Results <span className=" font-medium">({count})</span>
              </>
            ) : (
              <p>There are no results in this section</p>
            )}
          </span>
        </div>
        {props.children}
      </div>
    </Container>
  );
};

type VisibleSection = 'All' | 'Products' | 'Recipes' | 'Other';

type FilterButtonProps = {
  onClick: (value: VisibleSection) => void;
  filter: VisibleSection;
  currentFilter: VisibleSection;
  children?: React.ReactNode;
};
const FilterButton: React.FC<FilterButtonProps> = (props) => {
  const { onClick, filter, currentFilter } = props;
  return (
    <div
      className={classnames({
        'opacity-30 hover:opacity-75': filter !== currentFilter,
      })}
    >
      <OutlineButton
        onClick={() => {
          onClick(filter);
        }}
        buttonSize="small"
      >
        <span className="whitespace-nowrap font-normal normal-case">{props.children}</span>
      </OutlineButton>
    </div>
  );
};

const Page: NextPage<InferGetStaticPropsType<typeof getStaticProps>> = ({ data }) => {
  const { siteSettings } = data;
  const router = useRouter();
  const searchFieldRef = useRef(null);

  const [searchResult, setSearchResult] = useState<SFSearchResult>();
  const [query, setQuery] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const [section, setSection] = useState<VisibleSection>('All');
  const asNumber = (number: number) => number || 0;

  const searchTimeout = useRef(null);

  const total = () => {
    const add = (a: number, b: number) => {
      return a + b;
    };
    if (searchResult) {
      return [
        searchResult.content?.length,
        searchResult.products?.length,
        searchResult.recipes?.length,
      ]
        .filter((v) => !isNaN(v))
        .map(Number)
        .reduce(add, 0);
    }
    return 0;
  };

  const sectionIsVisible = (newSection: VisibleSection) => {
    return section === 'All' || section === newSection;
  };

  const onSearchTermChange = (event) => {
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      if (event.target.value.length > 2) {
        router.query.q = event.target.value;
        router.push(router, null, { shallow: true });
      }
    }, 500);
  };

  useEffect(() => {
    const searchField = searchFieldRef.current;
    if (router.query?.q) {
      searchField.value = router.query.q;
      setSearchTerm(router.query.q as string);
    }
  }, [router.query?.q]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchTerm?.length > 2) {
        setQuery(searchTerm);
      }
    }, 333);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  const changeSearchType = (value: VisibleSection) => {
    setSection(value);
  };

  const searchQuery = useQuery(
    ['search', query],
    async () => {
      const response = await sanfordApi.search(query);
      return response;
    },
    { enabled: !!query }
  );

  useEffect(() => {
    setSearchResult(searchQuery?.data?.data?.result);
  }, [searchQuery.data?.data?.result]);

  return (
    <Layout
      siteSettings={siteSettings}
      metaTags={[
        {
          name: 'robots',
          content: 'noindex',
        },
      ]}
      seo={{
        title: 'Search',
        description: 'Sanford search',
        openGraphImageUrl: null,
      }}
    >
      <Container>
        <div className="mb-6 border-b-2 border-sf-blue-700/5 pb-6">
          <label className="mb-12 flex h-20 items-center  gap-8 text-sf-blue-700 sm:mb-16">
            <SearchIcon className="h-10 w-10 flex-none" />
            <input
              ref={searchFieldRef}
              type="text"
              className="mt-3 min-w-0 max-w-full text-5xl sm:text-6xl"
              onChange={onSearchTermChange}
              placeholder="Search"
            ></input>
          </label>

          <div className="-mx-6 flex gap-4 overflow-auto px-6 sm:mx-auto sm:px-0 md:overflow-hidden">
            <FilterButton filter={'All'} currentFilter={section} onClick={changeSearchType}>
              All ({total()})
            </FilterButton>
            <FilterButton filter={'Products'} currentFilter={section} onClick={changeSearchType}>
              Products ({asNumber(searchResult?.products.length)})
            </FilterButton>
            <FilterButton filter={'Recipes'} currentFilter={section} onClick={changeSearchType}>
              Recipes ({asNumber(searchResult?.recipes.length)})
            </FilterButton>
            <FilterButton filter={'Other'} currentFilter={section} onClick={changeSearchType}>
              Other ({asNumber(searchResult?.content.length)})
            </FilterButton>
          </div>
        </div>
      </Container>
      {searchQuery.isLoading && (
        <div className="flex justify-center py-24">
          <SpinnerIcon className="animate-spin" />
        </div>
      )}
      {!searchQuery.isIdle && !searchQuery.isLoading && total() === 0 && (
        <Container>
          <p className="py-18 mx-auto text-center font-medium">
            No results. Try searching for something else.
          </p>
        </Container>
      )}
      {!searchQuery.isLoading && total() > 0 && (
        <>
          {sectionIsVisible('Products') && (
            <ResultSection title="Products" count={searchResult?.products.length}>
              <div className="grid grid-cols-1 gap-x-6 gap-y-6 lg:grid-cols-2 lg:gap-y-10 xl:grid-cols-3">
                {searchResult?.products.map((product, index) => (
                  <ProductCard
                    key={product?.id}
                    product={product}
                    index={index}
                    timestamp={index}
                  ></ProductCard>
                ))}
              </div>
            </ResultSection>
          )}
          {sectionIsVisible('Recipes') && (
            <ResultSection title="Recipes" count={searchResult?.recipes?.length}>
              <div className="grid grid-cols-1 gap-x-6 gap-y-6 lg:grid-cols-2 lg:gap-y-10 xl:grid-cols-3">
                {searchResult?.recipes.map((recipe, index) => (
                  <RecipeCard
                    key={recipe?.sys?.id}
                    recipe={recipe}
                    index={index}
                    timestamp={index} //todo: I think this is cuasing the cards to reflip every keystroke.
                    //we might be able to change this to something from the query so that it will flip only when a new query result is created?
                  ></RecipeCard>
                ))}
              </div>
            </ResultSection>
          )}
          {sectionIsVisible('Other') && (
            <ResultSection title="Information" count={searchResult?.content.length}>
              {searchResult?.content.length > 0 && (
                <div className="border-t border-sf-blue-700/5">
                  {searchResult?.content.map((content, index) => (
                    <div key={content.link} className="border-b border-sf-blue-700/5 px-6 py-8">
                      <div className="mb-4 text-2xl uppercase underline">
                        <Link internalUrl={content.link}>{content.title}</Link>
                      </div>

                      <p className="text-sm">{content.description}</p>
                    </div>
                  ))}
                </div>
              )}
            </ResultSection>
          )}
        </>
      )}
    </Layout>
  );
};

export const getStaticProps: GetStaticProps<{
  data: { siteSettings: SiteSettingsData };
}> = async () => {
  const siteSettings = await contentful.siteSettings.getSiteSettings();

  return {
    revalidate: REVALIDATION_TIME.SHORT,
    props: {
      data: { siteSettings },
    },
  };
};

export default Page;
